import { Diagnostic } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DIAGNOSTIC } from "@/constants/resultsLimits";

/** What a picker needs of a diagnostic. */
export interface CatalogDiagnostic {
  id: string;
  name: string;
}

/**
 * Every diagnostic of the database, once per session.
 *
 * Around 66,000 of them and 28 MB in a typical database, and Geotab ignores a property selector
 * here, so they are reduced to id and name right away and kept for the session: a picker that
 * filters while typing needs all of them, and a server search per keystroke cannot keep up.
 */
export function useDiagnosticCatalog(disabled?: boolean) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["diagnosticCatalog", activeSessionId],
    queryFn: async ({ signal }): Promise<CatalogDiagnostic[]> => {
      const diagnostics: Diagnostic[] = await geotabClient.get(
        "Diagnostic",
        {},
        RESULTS_LIMIT_DIAGNOSTIC,
        signal,
      );
      return diagnostics
        .map(diagnostic => ({ id: diagnostic.id, name: diagnostic.name ?? diagnostic.id }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !disabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * The diagnostics whose name or id contains every word of `query`, ignoring case.
 *
 * Words rather than the whole phrase, so "zelle temp max" finds "Zelltemperatur Maximum" no matter
 * in which order the database's name puts them.
 */
export function filterDiagnostics(catalog: CatalogDiagnostic[], query: string): CatalogDiagnostic[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return catalog;

  return catalog.filter(diagnostic => {
    const text = `${diagnostic.name} ${diagnostic.id}`.toLowerCase();
    return words.every(word => text.includes(word));
  });
}
